import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
import type { RootState } from "~/store";
import { fetchAsset } from "~/thunks/assetsThunks";
import type { Folder, SubTask, Task } from "~/types/bridge";
import { setAsset } from "./assetSlice";
import { fetchSurvey } from "~/thunks/surveyThunks";
import type { TasksTreeNode, TreeNode } from "~/types/tasks";

interface TasksState {
  filtersState: any[];
  loading: boolean;
  selectedImage: any | null;
  updatingSubTask: string;
  selectedFolder: Folder | null;
  selectedSubTask: SubTask | null;
  selectedTask: Task | null;
  openedFolders: any[];
  mode: any | null;
  collapsed: boolean;
  files: File[];
}

const initialState: TasksState = {
  filtersState: [],
  loading: false,
  selectedImage: null,
  selectedFolder: null,
  selectedSubTask: null,
  selectedTask: null,
  openedFolders: [],
  mode: null,
  collapsed: false,
  updatingSubTask: '',
  files: [],
};

const tasksSlice = createSlice({
  name: "tasks",
  initialState,
  reducers: {
    setFiltersState(state, action: PayloadAction<any[]>) {
      state.filtersState = action.payload;
    },
    setLoading(state, action: PayloadAction<boolean>) {
      state.loading = action.payload;
    },
    setSelectedImage(state, action: PayloadAction<any | null>) {
      state.selectedImage = action.payload;
    },
    setFiles(state, action: PayloadAction<File[]>) {
      state.files = action.payload;
    },
    setSelectedFolder(state, action: PayloadAction<Folder | null>) {
      state.selectedFolder = action.payload;
    },
    setSelectedSubTask(state, action: PayloadAction<SubTask |TreeNode | TasksTreeNode | null>) {
      state.selectedSubTask = action.payload as SubTask | null;
    },
    setSelectedTask(state, action: PayloadAction<Task | null>) {
      console.log("setSelectedTask", action.payload);
      state.selectedTask = action.payload;
    },
    setOpenedFolders(state, action: PayloadAction<any[]>) {
      state.openedFolders = action.payload;
    },
    setMode(state, action: PayloadAction<any | null>) {
      state.mode = action.payload;
    },
    setCollapsed(state, action: PayloadAction<boolean>) {
      state.collapsed = action.payload;
    },
    setUpdatingSubTask(state, action: PayloadAction<string>) {
      state.updatingSubTask = action.payload;
    },
    resetTasksState(state) {
      Object.assign(state, initialState);
    },
  },
   extraReducers: (builder) => {
    builder
      .addCase(setAsset, (state, action: PayloadAction<any>) => {
        Object.assign(state, initialState);
      })
      .addCase(fetchSurvey.pending, (state, action: PayloadAction<any>) => {
        Object.assign(state, initialState);
      })
    }
});

export const {
  setFiltersState,
  setLoading,
  setSelectedImage,
  setSelectedFolder,
  setSelectedSubTask,
  setSelectedTask,
  setOpenedFolders,
  setMode,
  setCollapsed,
  setUpdatingSubTask,
  resetTasksState,
} = tasksSlice.actions;

export default tasksSlice.reducer;

export const selectTasksState = (state: RootState) => state.tasks;
export const selectFiltersState = (state: RootState) => state.tasks.filtersState;
export const selectLoading = (state: RootState) => state.tasks.loading;
export const selectSelectedImage = (state: RootState) => state.tasks.selectedImage;
export const selectSelectedFolder = (state: RootState) => state.tasks.selectedFolder;
export const selectSelectedSubTask = (state: RootState) => state.tasks.selectedSubTask;
export const selectSelectedTask = (state: RootState) => state.tasks.selectedTask;
export const selectOpenedFolders = (state: RootState) => state.tasks.openedFolders;
export const selectMode = (state: RootState) => state.tasks.mode;
export const selectCollapsed = (state: RootState) => state.tasks.collapsed;
export const selectFiles = (state: RootState) => state.tasks.files;
export const selectUpdatingSubTask = (state: RootState) => state.tasks.updatingSubTask;
